import React from "react";
import { FaInfoCircle } from "react-icons/fa";
import { Col, Row } from "reactstrap";
import { useSelector } from "react-redux";
import CardColComponent from "../containers/genericCardComponent/CardColComponent";
import { numberWithCommas } from "../utils/customFunction";

interface OverallSummary {
  total_employees: number;
  total_net_amount: number;
  total_profit_distributed: number;
  total_new_amount: number;
  total_employee_contribution: number;
  total_employer_contribution: number;
}

interface InvestmentOption {
  investment_option: string;
  employee_count: number;
  total_net_amount: number;
  total_profit_allocated: number;
  profit_ratio: number;
  profit_percentage: number;
  total_new_amount: number;
}

interface ProfitPreviewCardProps {
  overallSummary: OverallSummary;
  investmentOptions: InvestmentOption[];
  fund?: string;
  profitMonth?: string;
  report?: boolean;
  reportName?: string;
}

const ProfitPreviewCard = ({
  overallSummary,
  investmentOptions,
  fund = "",
  profitMonth = "",
  report = false,
  reportName = "",
}: ProfitPreviewCardProps) => {
  const theme = useSelector((state: any) => state.ThemeModeReducer.Theme);

  // Top details similar to DividendCard
  const topDetails = [
    { title: "Fund", value: fund },
    { title: "Profit Month", value: profitMonth },
    { title: "Total Employees", value: overallSummary?.total_employees || 0 },
    {
      title: "Total Net Amount",
      value: overallSummary?.total_net_amount || 0,
      isAmount: true,
      fixed: 2,
    },
    {
      title: "Total Profit",
      value: overallSummary?.total_profit_distributed || 0,
      isAmount: true,
      fixed: 2,
    },
    {
      title: "Total New Amount",
      value: overallSummary?.total_new_amount || 0,
      isAmount: true,
      fixed: 2,
    },
  ];

  // Summary details similar to DividendCard summaryDetails
  const summaryDetails = [
    {
      title: "Total Employee Contribution",
      value: overallSummary?.total_employee_contribution || 0,
      isAmount: true,
      fixed: 2,
    },
    {
      title: "Total Employer Contribution",
      value: overallSummary?.total_employer_contribution || 0,
      isAmount: true,
      fixed: 2,
    },
    {
      title: "Total Net Amount",
      value: overallSummary?.total_net_amount || 0,
      isAmount: true,
      fixed: 2,
    },
    {
      title: "Total Profit Distributed",
      value: overallSummary?.total_profit_distributed || 0,
      isAmount: true,
      fixed: 2,
    },
    {
      title: "Total New Amount",
      value: overallSummary?.total_new_amount || 0,
      isAmount: true,
      fixed: 2,
    },
  ];

  return (
    <Row>
      {/* Top Details Card */}
      <Col xs={12}>
        <div
          className={`${
            report ? "" : "card border border-light shadow-sm"
          } m-1 p-2 small`}
        >
          <div
            className={`${
              report ? "" : "shadow"
            } card-header py-2 px-3 border-bottom`}
          >
            <div
              className={`${
                report ? "text-black" : ""
              } d-flex align-items-center`}
            >
              <FaInfoCircle className="mr-2" />
              <span>Profit Preview - Overview</span>
            </div>
          </div>
          <div
            className={`${
              report ? "text-black" : theme === "dark" ? "text-white" : ""
            } card-body p-2`}
          >
            <div className="d-flex flex-wrap">
              {topDetails.map((item: any, i) => (
                <div className="flex-item pr-5 pb-3" key={i}>
                  <CardColComponent
                    title={item.title}
                    subTitle={
                      item.isAmount
                        ? numberWithCommas(
                            parseFloat(item.value ?? 0).toFixed(item.fixed)
                          )
                        : item.value
                    }
                    amount={item.isAmount}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Col>

      {/* Summary Details Card */}
      {/* <Col xs={12}>
        <div
          className={`${
            report ? "" : "card border border-light shadow-sm"
          } m-1 p-2 small`}
        >
          <div
            className={`${
              report ? "" : "shadow"
            } card-header py-2 px-3 border-bottom`}
          >
            <div
              className={`${
                report ? "text-black" : ""
              } d-flex align-items-center`}
            >
              <FaInfoCircle className="mr-2" />
              <span>{report ? "Profit Totals" : "Profit Summary"}</span>
            </div>
          </div>
          <div
            className={`${
              report ? "text-black" : theme === "dark" ? "text-white" : ""
            } card-body p-2`}
          >
            <div className="d-flex flex-wrap">
              {summaryDetails.map((item: any, i) => (
                <div className="flex-item pr-5 pb-3" key={i}>
                  <CardColComponent
                    title={item.title}
                    subTitle={numberWithCommas(
                      parseFloat(item.value ?? 0).toFixed(item.fixed)
                    )}
                    amount={item.isAmount}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Col> */}

      {/* Investment Options Cards */}
      {investmentOptions && investmentOptions.length > 0 && (
        <>
          {investmentOptions.map((option, idx) => {
            const optionDetails = [
              { title: "Employee Count", value: option.employee_count },
              {
                title: "Net Amount",
                value: option.total_net_amount,
                isAmount: true,
                fixed: 2,
              },
              {
                title: "Profit Allocated",
                value: option.total_profit_allocated,
                isAmount: true,
                fixed: 2,
              },
              {
                title: "Profit Ratio",
                value: option.profit_ratio,
                normal_num: true,
                fixed: 6,
              },
              {
                title: "New Amount",
                value: option.total_new_amount,
                isAmount: true,
                fixed: 2,
              },
            ];

            return (
              <Col xs={12} key={idx}>
                <div
                  className={`${
                    report ? "" : "card border border-light shadow-sm"
                  } m-1 p-2 small`}
                >
                  <div
                    className={`${
                      report ? "" : "shadow"
                    } card-header py-2 px-3 border-bottom`}
                  >
                    <div
                      className={`${
                        report ? "text-black" : ""
                      } d-flex align-items-center`}
                    >
                      <FaInfoCircle className="mr-2" />
                      <span>Investment Option: {option.investment_option}</span>
                    </div>
                  </div>
                  <div
                    className={`${
                      report
                        ? "text-black"
                        : theme === "dark"
                        ? "text-white"
                        : ""
                    } card-body p-2`}
                  >
                    <div className="d-flex flex-wrap">
                      {optionDetails.map((item: any, i) => (
                        <div className="flex-item pr-5 pb-3" key={i}>
                          <CardColComponent
                            title={item.title}
                            subTitle={
                              item.fixed !== undefined
                                ? item.percentage
                                  ? `${numberWithCommas(
                                      parseFloat(item.value ?? 0).toFixed(
                                        item.fixed
                                      )
                                    )}%`
                                  : numberWithCommas(
                                      parseFloat(item.value ?? 0).toFixed(
                                        item.fixed
                                      )
                                    )
                                : item.value
                            }
                            amount={item?.isAmount}
                            percentage={item?.percentage}
                            normal_num={item?.normal_num}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
        </>
      )}
    </Row>
  );
};

export default ProfitPreviewCard;
